import { useState, useEffect, useCallback } from "react";
import Landing from "./Landing";
import App from "./App";
import Login from "./Login";
import Legal from "./Legal";
import Guide from "./Guide";
import Pricing from "./Pricing";
import Welcome from "./Welcome";
import Demo from "./Demo";

export default function Router() {
  const [page, setPage] = useState("loading");
  const [userEmail, setUserEmail] = useState("");
  const [userName, setUserName] = useState("");

  const fetchName = useCallback(async (email) => {
    try {
      const r = await fetch(`/api/user-profile?email=${encodeURIComponent(email)}`);
      const data = await r.json();
      return data.name || "";
    } catch {
      return "";
    }
  }, []);

  const grantAccess = useCallback(async (email, isNewLogin = false) => {
    localStorage.setItem("pts_email", email);
    localStorage.setItem("pts_access", "true");
    setUserEmail(email);

    // Fetch name from Upstash
    const name = await fetchName(email);
    setUserName(name);

    if (isNewLogin && !name) {
      // First time — show welcome screen
      setPage("welcome");
    } else {
      setPage("app");
      window.history.replaceState({}, "", "/app");
    }
  }, [fetchName]);

  const verifyToken = useCallback(async (token) => {
    try {
      const res = await fetch("/api/auth-verify-token", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const data = await res.json();
      if (data.valid && data.email) {
        // Store the 30-day session token. /api/claude (and any future gated
        // endpoint) requires this as `Authorization: Bearer <token>`.
        if (data.sessionToken) {
          localStorage.setItem("pts_session", data.sessionToken);
        }
        await grantAccess(data.email, true);
      } else {
        const reason = data.reason === "expired" ? "expired" : "invalid";
        setPage("login");
        window.history.replaceState({}, "", "/login?error=" + reason);
      }
    } catch {
      setPage("login");
    }
  }, [grantAccess]);

  const verifyStripeSession = useCallback(async (sessionId) => {
    try {
      const res = await fetch("/api/verify-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId }),
      });
      const data = await res.json();
      if ((data.active || data.success) && data.email) {
        if (data.sessionToken) {
          localStorage.setItem("pts_session", data.sessionToken);
        }
        await grantAccess(data.email, true);
      } else {
        setPage("landing");
      }
    } catch {
      setPage("landing");
    }
  }, [grantAccess]);

  const checkSubscription = useCallback(async (email) => {
    try {
      const res = await fetch("/api/auth-check", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (data.active) {
        const name = await fetchName(email);
        setUserEmail(email);
        setUserName(name);
        setPage("app");
        window.history.replaceState({}, "", "/app");
      } else {
        localStorage.removeItem("pts_email");
        localStorage.removeItem("pts_access");
        localStorage.removeItem("pts_session");
        setPage("landing");
      }
    } catch {
      const savedEmail = localStorage.getItem("pts_email");
      if (savedEmail) {
        const name = await fetchName(savedEmail);
        setUserEmail(savedEmail);
        setUserName(name);
        setPage("app");
      } else {
        setPage("landing");
      }
    }
  }, [fetchName]);

  useEffect(() => {
    const path = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    const sessionId = params.get("session_id");

    // Legal pages work for everyone — check first, no auth needed
    if (path === "/privacy") { setPage("privacy"); return; }
    if (path === "/terms") { setPage("terms"); return; }
    if (path === "/guide") { setPage("guide"); return; }
    if (path === "/pricing") { setPage("pricing"); return; }

    if (token) { verifyToken(token); return; }
    if (sessionId) { verifyStripeSession(sessionId); return; }

    const savedEmail = localStorage.getItem("pts_email");
    const savedAccess = localStorage.getItem("pts_access");

    if (savedEmail && savedAccess === "true") {
      checkSubscription(savedEmail);
      return;
    }

    if (path === "/login") setPage("login");
    else setPage("landing");
  }, [verifyToken, verifyStripeSession, checkSubscription]);

  const handleLogout = () => {
    localStorage.removeItem("pts_email");
    localStorage.removeItem("pts_access");
    localStorage.removeItem("pts_session");
    setUserEmail("");
    setUserName("");
    setPage("landing");
    window.history.replaceState({}, "", "/");
  };

  const goToLogin = () => {
    // If the user is already signed in (localStorage has both flags), don't
    // make them re-enter their email — route straight to /app via the normal
    // subscription check. This handles the "I'm signed in on another tab and
    // clicked Sign In on this one" case.
    const savedEmail = localStorage.getItem("pts_email");
    const savedAccess = localStorage.getItem("pts_access");
    const savedSession = localStorage.getItem("pts_session");
    if (savedEmail && savedAccess === "true" && savedSession) {
      setPage("loading");
      checkSubscription(savedEmail);
      return;
    }
    setPage("login");
    window.history.replaceState({}, "", "/login");
  };

  const goToLanding = () => {
    setPage("landing");
    window.history.replaceState({}, "", "/");
  };



  const handleWelcomeDone = async (name) => {
    if (name) setUserName(name);
    setPage("app");
    window.history.replaceState({}, "", "/app");
  };

  if (page === "loading") {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f7f7f5" }}>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{ width: 32, height: 32, border: "3px solid #e8e8e4", borderTopColor: "#3b5bdb", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      </div>
    );
  }

  if (page === "demo") return <Demo onSubscribe={goToLanding} onBack={goToLanding} />;
  if (page === "guide") return <Guide onLogin={goToLogin} />;
  if (page === "pricing") return <Pricing onLogin={goToLogin} />;
  if (page === "privacy") return <Legal type="privacy" onBack={() => { window.history.back(); }} />;
  if (page === "terms") return <Legal type="terms" onBack={() => { window.history.back(); }} />;
  if (page === "welcome") return <Welcome email={userEmail} onDone={handleWelcomeDone} />;
  if (page === "app") return <App onLogout={handleLogout} onHome={() => setPage("app")} userEmail={userEmail} userName={userName} />;
  if (page === "login") return <Login onSuccess={(email) => grantAccess(email, true)} onBack={goToLanding} />;
  return <Landing onGetStarted={goToLogin} onLogin={goToLogin} onPreview={() => setPage('demo')} />;
}
